<template>
  <div class="pdb-20px" v-if="ecritures">
    <div class="card shadow pd-20px">
      <div class="columns is-multiline">
        <div class="column is-3">
          <b-field :label="$t('ecritureCompta.libelleEcriture')"
                    custom-class="has-text-weight-bold is-marginless">
            <span class="has-text-default"> {{ecritures.libelle}} </span>
          </b-field>
        </div>
        <div class="column is-2">
          <b-field :label="$t('ecritureCompta.date')"
                    custom-class="has-text-weight-bold is-marginless">
            <span class="has-text-default"> {{ecritures.dateComptable | formatDate}} </span>
          </b-field>
        </div>
        <div class="column is-2">
          <b-field :label="$t('ecritureCompta.journal')"
                    custom-class="has-text-weight-bold is-marginless">
            <span class="has-text-default"> {{ecritures.journal}} </span>
          </b-field>
        </div>
        <div class="column is-2">
          <b-field :label="$t('ecritureCompta.etat')"
                    custom-class="has-text-weight-bold is-marginless">
            <span class="has-text-default">
              {{ecritures.isValid ? $t('ecritureCompta.etatValide')
                                  : $t('ecritureCompta.etatNonValide')}}
            </span>
          </b-field>
        </div>
        <div class="column">
          <b-field :label="$t('ecritureCompta.lot')"
                    custom-class="has-text-weight-bold is-marginless">
            <span class="has-text-default" v-if="!ecritures.ifLot">
              {{$t('ecritureCompta.aucunLot')}}
            </span>
            <div class="has-text-default" v-else>
              <router-link v-if="activeButtonEtLien" :to="{
                  name: 'c.comptabilite.lots_comptable',
                  params: { id: ecritures.ifLot.id }
                }"
                           :class="{'text-access-no-permission':
                !checkPermissions(permLotsComptable.lecture)}"
                           class="lien">
                {{ecritures.ifLot.libelle}}
              </router-link>
              <span v-else>{{ecritures.ifLot.libelle}}</span> <br>
              <span v-if="ecritures.ifLot.etat === transmis">
                {{$t('ecritureCompta.transmisAu')}}
                {{ecritures.ifLot.dateTransfert | formatDate}}
              </span>
            </div>
          </b-field>
        </div>
      </div>
      <v-client-table :data="ecritures.ifLignesEcriture"
                      :columns="columns" class="tableWithChildRow"
                      :options="options"
                      v-if="ecritures.ifLignesEcriture && ecritures.ifLignesEcriture.length !== 0">
        <div slot="libelle" slot-scope="props" style="">
          {{props.row.libelle}}
        </div>
        <div slot="debit" slot-scope="props" class="is-pulled-right">
          {{props.row.debit | formatDevise}}
        </div>
        <div slot="credit" slot-scope="props" class="is-pulled-right">
          {{props.row.credit | formatDevise}}
        </div>
        <div slot="dateDebut" slot-scope="props">
          {{props.row.dateDebut | formatDate}}
        </div>
        <div slot="dateFin" slot-scope="props">
          {{props.row.dateFin | formatDate}}
        </div>
        <template slot='appendBody'>
          <table-footer-totaux
            ref="tableTotaux"
            :columns="columns"
            :target="targetTotal"
            :items="ecritures.ifLignesEcriture">
          </table-footer-totaux>
        </template>
      </v-client-table>
      <error-msg-component :messages="validation(data.erreurs)"
                           :error="data.erreurs && data.erreurs.length > 0"
                           :withEmplyFields="true"/>
    </div>
  </div>
</template>

<script>

import { createNamespacedHelpers } from 'vuex';
import cloneDeep from 'lodash/cloneDeep';
import validationEcriture from '@/services/ecritureComptable/validation';
import { TRANSMIS } from '@/configuration/compta/lotConf';
import {
  TEXTS, SORT_ICON, NO_PER_PAGE_VALUE, RESULTS_PER_PAGE,
} from '@/configuration/clientTableConf';
import errorMsgComponent from '@/components/general/errorMsgComponent';
import { LOTS_COMPTABLE } from '@/configuration/compta/permissionsComptaConf';
import detailLigneAnalytique from '@/components/factures/detailLigneAnalytique';
import tableFooterTotaux from '@/components/general/tableFooterTotaux';

const Permissions = createNamespacedHelpers('permissions');

export default {
  props: {
    data: Object,
  },
  components: {
    errorMsgComponent,
    tableFooterTotaux,
  },
  data() {
    return {
      columns: ['libelle', 'compte', 'compteAuxiliaire', 'reference', 'debit', 'credit', 'dateDebut', 'dateFin'],
      transmis: TRANSMIS,
      ecritures: null,
      permLotsComptable: LOTS_COMPTABLE,
    };
  },
  computed: {
    ...Permissions.mapGetters({
      checkPermissions: 'checkPermissions',
    }),
    options() {
      const texts = cloneDeep(TEXTS);
      texts.count = '';
      return {
        sortIcon: SORT_ICON,
        headings: {
          libelle: this.$t('ecritureCompta.tabHeader.libelle'),
          compte: this.$t('ecritureCompta.tabHeader.compte'),
          debit: this.$t('ecritureCompta.tabHeader.debit'),
          credit: this.$t('ecritureCompta.tabHeader.credit'),
          dateDebut: this.$t('ecritureCompta.tabHeader.debut'),
          dateFin: this.$t('ecritureCompta.tabHeader.fin'),
          compteAuxiliaire: this.$t('ecritureCompta.tabHeader.compteAuxiliaire'),
          reference: this.$t('ecritureCompta.tabHeader.reference'),
        },
        perPageValues: NO_PER_PAGE_VALUE,
        perPage: RESULTS_PER_PAGE,
        texts: texts,
        filterable: false,
        sortable: ['libelle', 'compte', 'debit', 'credit', 'dateDebut', 'dateFin', 'compteAuxiliaire', 'reference'],
        childRow: this.haschildRow ? detailLigneAnalytique : null,
        rowClassCallback: ((row) => {
          if (!row.ifLignesEcritureAna || row.ifLignesEcritureAna.length === 0) {
            return 'hideChildToggler pointer-events-none';
          }
          return '';
        }),
        columnsClasses: {
          compte: 'colonneWidth150px',
          compteAuxiliaire: 'colonneWith180',
          reference: 'colonneWidth150px',
          credit: 'colonneWidth150px',
          debit: 'colonneWidth150px',
          dateDebut: 'colonneWidth130px',
          dateFin: 'colonneWidth130px',
          libelle: 'is-word-break width-250',
        },
      };
    },
    targetTotal() {
      return ['debit', 'credit'];
    },
    haschildRow() {
      return this.ecritures.ifLignesEcriture.some((el) => {
        return el.ifLignesEcritureAna && el.ifLignesEcritureAna.length > 0;
      });
    },
    activeButtonEtLien() {
      return this.checkIfCurrentSociety(this.data.societeId);
    },
  },
  methods: {
    validation(erreurs) {
      const messages = [];
      if (erreurs) validationEcriture(this.$i18n, erreurs, messages);
      return messages;
    },
  },
  mounted() {
    this.ecritures = cloneDeep(this.data);
  },
};
</script>
